import * as React from "react";

import { api } from "@repo/backend";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui/components/avatar";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/command";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@repo/ui/components/field";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/table";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import {
  CheckIcon,
  ChevronDownIcon,
  Loader2Icon,
  MessageSquarePlusIcon,
  RefreshCwIcon,
  Settings2Icon,
  UsersIcon,
} from "lucide-react";
import { toast } from "sonner";

import {
  DiscordChannelSelect,
  type DiscordChannelRow,
} from "@/components/settings/discord-channel-select";

export const Route = createFileRoute("/_authed/setting/schedule")({
  head: () => ({ meta: [{ title: "Call times" }] }),
  component: RouteComponent,
});

type RoleRow = {
  id: string;
  name: string;
  color: string;
  position: number;
  managed: boolean;
};

type RosterMemberData = {
  id: string;
  username: string;
  displayName: string;
  avatar: string | null;
  roles: string[];
  workgrid: { userId: string; name: string; role: string; isActive: boolean } | null;
};

type MemberGroup = {
  _id: string;
  discordUserId: string;
  groupKey: string;
  displayName: string;
  username: string;
};

const GROUP_OPTIONS = [
  { key: "", label: "Unassigned" },
  { key: "ec-photographer", label: "EC — Photographer" },
  { key: "ec-editor", label: "EC — Editor" },
  { key: "md-photographer", label: "MD — Photographer" },
  { key: "md-editor", label: "MD — Editor" },
] as const;

function DiscordRoleMultiSelect({
  roles,
  selectedRoleIds,
  onSelectionChange,
  disabled = false,
}: {
  roles: RoleRow[];
  selectedRoleIds: string[];
  onSelectionChange: (roleIds: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const selectedRoles = React.useMemo(
    () => roles.filter((role) => selectedRoleIds.includes(role.id)),
    [roles, selectedRoleIds],
  );
  const filteredRoles = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return roles;
    return roles.filter((role) => role.name.toLowerCase().includes(query));
  }, [roles, search]);

  const toggleSelection = React.useCallback(
    (roleId: string) => {
      if (selectedRoleIds.includes(roleId)) {
        onSelectionChange(selectedRoleIds.filter((id) => id !== roleId));
        return;
      }

      onSelectionChange([...selectedRoleIds, roleId]);
    },
    [onSelectionChange, selectedRoleIds],
  );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="h-auto w-full justify-between px-3 py-2"
            disabled={disabled}
          />
        }
      >
        <div className="flex min-h-6 flex-wrap items-center gap-1.5">
          {selectedRoles.length === 0 ? (
            <span className="text-sm text-muted-foreground">Select call-times roles...</span>
          ) : selectedRoles.length > 3 ? (
            <Badge variant="secondary">{selectedRoles.length} roles selected</Badge>
          ) : (
            selectedRoles.map((role) => (
              <Badge key={role.id} variant="secondary">
                {role.name}
              </Badge>
            ))
          )}
        </div>
        <ChevronDownIcon className="size-4 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-105 p-0">
        <Command shouldFilter={false} className="p-1">
          <CommandInput placeholder="Search roles..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-72">
            {filteredRoles.length === 0 ? (
              <CommandEmpty>No roles found.</CommandEmpty>
            ) : (
              <CommandGroup heading="Discord roles">
                {filteredRoles.map((role) => {
                  const selected = selectedRoleIds.includes(role.id);

                  return (
                    <CommandItem
                      key={role.id}
                      value={role.name}
                      onSelect={() => toggleSelection(role.id)}
                      className="cursor-pointer gap-2"
                    >
                      <span
                        className="size-2.5 rounded-full border"
                        style={{
                          backgroundColor: role.color === "#000000" ? "transparent" : role.color,
                        }}
                      />
                      <span className="flex-1">{role.name}</span>
                      {role.managed ? <Badge variant="outline">Managed</Badge> : null}
                      {selected ? <CheckIcon className="size-4" /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RouteComponent() {
  const confirm = useConfirmationDialog();
  const queryConfig = useQuery(api.discord.getConfig, {});
  const upsertScheduleConfig = useMutation(api.discord.upsertScheduleConfig);
  const listRoles = useAction(api.discord.listRoles);
  const listChannels = useAction(api.discord.listChannels);
  const refreshRoster = useAction(api.discord.refreshRoster);
  const rosterQuery = useQuery(api.discord.listRoster, {});
  const seedStages = useMutation(api.schedule.seedScheduleStages);
  const memberGroups = useQuery(api.schedule.listMemberGroups);
  const assignMemberToGroup = useMutation(api.schedule.assignMemberToGroup).withOptimisticUpdate(
    (store, args) => {
      const groups = store.getQuery(api.schedule.listMemberGroups, {});
      const current = groups?.find((group) => group.discordUserId === args.discordUserId);
      if (!groups || !current) return;
      store.setQuery(
        api.schedule.listMemberGroups,
        {},
        groups.map((group) => (group._id === current._id ? { ...group, ...args } : group)),
      );
    },
  );
  const removeMemberFromGroup = useMutation(
    api.schedule.removeMemberFromGroup,
  ).withOptimisticUpdate((store, { discordUserId }) => {
    const groups = store.getQuery(api.schedule.listMemberGroups, {});
    if (!groups) return;
    store.setQuery(
      api.schedule.listMemberGroups,
      {},
      groups.filter((group) => group.discordUserId !== discordUserId),
    );
  });

  const [guild, setGuild] = React.useState<{ id: string; name: string } | null>(null);
  const [roles, setRoles] = React.useState<RoleRow[]>([]);
  const [roleIds, setRoleIds] = React.useState<string[]>([]);
  const [channels, setChannels] = React.useState<DiscordChannelRow[]>([]);
  const [destinationChannelId, setDestinationChannelId] = React.useState<string | undefined>();
  const [isLoadingRoles, setIsLoadingRoles] = React.useState(false);
  const [isLoadingChannels, setIsLoadingChannels] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isRefreshingRoster, setIsRefreshingRoster] = React.useState(false);
  const [isSeeding, setIsSeeding] = React.useState(false);

  const rosterMembers = React.useMemo<RosterMemberData[]>(
    () => (rosterQuery?.ok ? ((rosterQuery.members ?? []) as RosterMemberData[]) : []),
    [rosterQuery],
  );

  const groupByDiscordId = React.useMemo(() => {
    const map = new Map<string, MemberGroup>();
    for (const g of memberGroups ?? []) {
      map.set(g.discordUserId, g);
    }
    return map;
  }, [memberGroups]);

  React.useEffect(() => {
    if (!queryConfig) return;
    setRoleIds(queryConfig.schedule.roleIds ?? []);
    setDestinationChannelId(queryConfig.schedule.destinationChannelId);
  }, [queryConfig]);

  const loadRoles = React.useCallback(async () => {
    setIsLoadingRoles(true);
    try {
      const result = await listRoles({ guildId: queryConfig?.guildId });

      if (!result.ok) {
        toast.error(result.error ?? "Failed to load Discord roles");
        setGuild(null);
        setRoles([]);
        return;
      }

      setGuild(result.guild ?? null);
      setRoles(result.roles as RoleRow[]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load Discord roles");
      setGuild(null);
      setRoles([]);
    } finally {
      setIsLoadingRoles(false);
    }
  }, [listRoles, queryConfig?.guildId]);

  React.useEffect(() => {
    if (!queryConfig) return;
    void loadRoles();
  }, [queryConfig !== undefined, queryConfig?.guildId, loadRoles]);

  const loadChannels = React.useCallback(async () => {
    setIsLoadingChannels(true);
    try {
      const result = await listChannels({ guildId: queryConfig?.guildId });

      if (!result.ok) {
        toast.error(result.error ?? "Failed to load Discord channels", {
          id: "discord-channels-load-error",
        });
        setChannels([]);
        return;
      }

      toast.dismiss("discord-channels-load-error");
      setChannels(result.channels as DiscordChannelRow[]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load Discord channels", {
        id: "discord-channels-load-error",
      });
      setChannels([]);
    } finally {
      setIsLoadingChannels(false);
    }
  }, [listChannels, queryConfig?.guildId]);

  React.useEffect(() => {
    if (!queryConfig) return;
    void loadChannels();
  }, [queryConfig !== undefined, queryConfig?.guildId, loadChannels]);

  const loadRoster = React.useCallback(async () => {
    if (roleIds.length === 0) return;
    setIsRefreshingRoster(true);
    try {
      const result = await refreshRoster({
        guildId: queryConfig?.guildId,
        channelId: destinationChannelId,
        roleIds,
      });
      if (!result.ok) {
        toast.error(result.error ?? "Failed to refresh roster");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to refresh roster");
    } finally {
      setIsRefreshingRoster(false);
    }
  }, [destinationChannelId, refreshRoster, queryConfig?.guildId, roleIds]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await upsertScheduleConfig({
        schedule: {
          destinationChannelId,
          roleIds,
        },
      });
      toast.success("Schedule settings saved");
    } catch (error) {
      if (error instanceof ConvexError && error.data === "FORBIDDEN") {
        toast.error("Only admins can update schedule settings.");
        return;
      }

      toast.error(error instanceof Error ? error.message : "Failed to save schedule settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSeedStages = async () => {
    const confirmed = await confirm({
      title: "Initialize stages",
      description:
        "This will DELETE all existing stages and create 30 pre-configured stages (18 Event Coverage + 12 Media Day). Existing stage assignments will also be removed.",
      confirmText: "Initialize",
      confirmButtonVariant: "destructive",
    });

    if (!confirmed) return;

    setIsSeeding(true);
    try {
      const result = await seedStages({});
      toast.success(`Created ${result.created} stages across Event Coverage and Media Day.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to seed stages");
    } finally {
      setIsSeeding(false);
    }
  };

  const handleGroupChange = async (member: RosterMemberData, groupKey: string) => {
    if (!groupKey) {
      try {
        await removeMemberFromGroup({ discordUserId: member.id });
      } catch {
        toast.error("Failed to remove from group");
      }
      return;
    }

    try {
      await assignMemberToGroup({
        discordUserId: member.id,
        groupKey,
        displayName: member.displayName,
        username: member.username,
      });
    } catch {
      toast.error("Failed to assign to group");
    }
  };

  const selectedRoles = roles.filter((role) => roleIds.includes(role.id));
  const missingRoleCount = roleIds.filter(
    (roleId) => !roles.some((role) => role.id === roleId),
  ).length;

  const assignedCount = rosterMembers.filter((m) => groupByDiscordId.has(m.id)).length;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4">
      <div className="flex flex-col gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Settings2Icon className="size-5" />
          Call times Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Configure Discord roles, refresh the roster, assign people to groups, and initialize
          stages.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquarePlusIcon className="size-4" />
            Destination Channel Routing
          </CardTitle>
          <CardDescription>Configure where schedule publish messages are sent.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-lg border px-3 py-2">
            <div className="text-sm">
              {guild ? (
                <>
                  <span className="text-muted-foreground">Connected server:</span>
                  <span className="ml-2 font-medium">{guild.name}</span>
                </>
              ) : (
                <span className="text-muted-foreground">Could not load Discord channels yet.</span>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadChannels()}
              disabled={isLoadingChannels}
            >
              {isLoadingChannels ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : (
                <RefreshCwIcon className="size-3.5" />
              )}
              Refresh Channels
            </Button>
          </div>

          <Field>
            <FieldLabel>
              <FieldContent>
                <FieldTitle>Schedule publish channel</FieldTitle>
                <FieldDescription>
                  Call-times schedule messages are sent to this Discord channel.
                </FieldDescription>
              </FieldContent>
            </FieldLabel>
            <DiscordChannelSelect
              channels={channels}
              selectedChannelId={destinationChannelId}
              onSelectionChange={setDestinationChannelId}
              disabled={isLoadingChannels}
              placeholder="Select schedule publish channel..."
            />
          </Field>
        </CardContent>
      </Card>

      {/* Role selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="size-4" />
            Call Times Roster
          </CardTitle>
          <CardDescription>
            The roster starts from members of the configured schedule channel who have any selected
            Discord role.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-lg border px-3 py-2">
            <div className="text-sm">
              {guild ? (
                <>
                  <span className="text-muted-foreground">Connected server:</span>
                  <span className="ml-2 font-medium">{guild.name}</span>
                </>
              ) : (
                <span className="text-muted-foreground">Could not load Discord roles yet.</span>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadRoles()}
              disabled={isLoadingRoles}
            >
              {isLoadingRoles ? (
                <Loader2Icon className="size-3.5 animate-spin" />
              ) : (
                <RefreshCwIcon className="size-3.5" />
              )}
              Refresh Roles
            </Button>
          </div>

          <Field>
            <FieldLabel>
              <FieldContent>
                <FieldTitle>Call-times Discord roles</FieldTitle>
                <FieldDescription>
                  Select at least one role. If this is empty, the schedule roster should have no
                  assignable people.
                </FieldDescription>
              </FieldContent>
            </FieldLabel>
            <DiscordRoleMultiSelect
              roles={roles}
              selectedRoleIds={roleIds}
              onSelectionChange={setRoleIds}
              disabled={isLoadingRoles}
            />
          </Field>

          <div
            className={cn(
              "rounded-lg border p-3 text-sm",
              roleIds.length === 0 &&
                "border-amber-300/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
            )}
          >
            {roleIds.length === 0 ? (
              "Select at least one Discord role before building the call-times roster."
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">Selected roles:</span>
                {selectedRoles.map((role) => (
                  <Badge key={role.id} variant="secondary">
                    {role.name}
                  </Badge>
                ))}
                {missingRoleCount > 0 ? (
                  <Badge variant="outline">{missingRoleCount} missing</Badge>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Refresh roster from Discord, then assign each person to a group below.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadRoster()}
              disabled={isRefreshingRoster || roleIds.length === 0}
            >
              {isRefreshingRoster ? <Loader2Icon className="size-3.5 animate-spin" /> : null}
              {isRefreshingRoster ? "Refreshing..." : "Refresh Roster"}
            </Button>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => void handleSave()}
              disabled={isSaving || queryConfig === undefined}
            >
              {isSaving ? <Loader2Icon className="size-4 animate-spin" /> : null}
              Save schedule settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Group assignment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="size-4" />
            Group Assignments
          </CardTitle>
          <CardDescription>
            Assign each person to a group. Unassigned people won't appear on the schedule board.
            {assignedCount > 0 ? ` ${assignedCount} of ${rosterMembers.length} assigned.` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rosterMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Refresh the roster above first, then assign people to groups here.
            </p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Discord user</TableHead>
                    <TableHead>WorkGrid</TableHead>
                    <TableHead className="w-48">Group</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rosterMembers.map((member) => {
                    const group = groupByDiscordId.get(member.id);

                    return (
                      <TableRow key={member.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="size-6">
                              <AvatarImage src={member.avatar ?? undefined} />
                              <AvatarFallback className="text-xs">
                                {member.displayName.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{member.displayName}</span>
                            <span className="text-xs text-muted-foreground">
                              @{member.username}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {member.workgrid ? (
                            <div className="flex items-center gap-1.5">
                              <Badge variant="secondary">{member.workgrid.name}</Badge>
                              {!member.workgrid.isActive ? (
                                <Badge variant="outline">Inactive</Badge>
                              ) : null}
                            </div>
                          ) : (
                            <Badge variant="outline">Needs account</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <select
                            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
                            value={group?.groupKey ?? ""}
                            onChange={(e) => void handleGroupChange(member, e.target.value)}
                          >
                            {GROUP_OPTIONS.map((opt) => (
                              <option key={opt.key} value={opt.key}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stage initialization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2Icon className="size-4" />
            Stages
          </CardTitle>
          <CardDescription>
            Initialize all 30 pre-configured stages for Event Coverage and Media Day.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            This creates 18 Event Coverage stages (9 scenes × photographer + editor) and 12 Media
            Day stages (6 sets × photographer + editor). Existing stages and assignments will be
            removed.
          </p>
          <Button
            variant="destructive"
            onClick={() => void handleSeedStages()}
            disabled={isSeeding}
          >
            {isSeeding ? <Loader2Icon className="size-4 animate-spin" /> : null}
            Initialize 30 Stages
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
